import axios from 'axios';
import toast from 'react-hot-toast';
import { tokenManager } from '@/auth';
import { AUTH_REFRESH_URL } from '@gokrew/models/constants';
import { ReportingErrorCode } from '@gokrew/models/reporting';
import * as Sentry from '@sentry/react';
import {
  getUserErrorMessage,
  getSentryErrorMessage,
} from '@gokrew/ui/utility/utility';
import i18n from '@/i18n';
import { BILLING_SEAT_LIMIT_EVENT } from '@/constants/billing';
import { getSeatLimitDetails } from '@/utils/billing';
import { isCeilingRefusal } from '@/utils/roleCeiling';

const api = axios.create({
  baseURL: '/api/v1',
  headers: { 'Content-Type': 'application/json' },
});

// Send the active app language to the backend so translated validation/business
// error messages come back in the user's selected locale.
api.interceptors.request.use((config) => {
  config.params = { lang: i18n.language || 'en', ...config.params };
  return config;
});

// Attach a valid token automatically, refreshing first if close to expiry.
// Only refreshes when a token already exists in memory — the request interceptor
// is responsible for keeping a live session fresh, not for restoring one from
// scratch (that is AuthContext.restoreSession()'s job on mount).
api.interceptors.request.use(async (config) => {
  // Skip token injection for the refresh endpoint itself to avoid loops
  if (config.url?.includes(AUTH_REFRESH_URL)) return config;

  // No token in memory means no active session — skip refresh entirely
  if (!tokenManager.getToken()) return config;

  const token = await tokenManager.getValidToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle errors globally
api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const status = error.response?.status;
    const hasToken = tokenManager.getToken() !== null;
    const url = error.config?.url;

    // handle 401 session expired / token refresh
    if (status === 401 && hasToken) {
      const originalRequest = error.config;

      // Prevent infinite retry loop — if this request already retried once, give up
      if (originalRequest._retry) {
        tokenManager.triggerSessionExpired();
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      // getValidToken() uses a shared refreshPromise, so concurrent 401s
      // all join the same in-flight refresh instead of firing multiple requests
      const newToken = await tokenManager.getValidToken();

      if (newToken) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      }

      tokenManager.triggerSessionExpired();
      return Promise.reject(error);
    }

    // Direct-message failures are handled inline. Never serialize message
    // request bodies into generic telemetry or redirect away from the draft.
    if (/\/(?:messaging|workflow-communications)(?:\/|\?|$)/.test(url ?? '')) {
      return Promise.reject(error);
    }

    // 403 Forbidden — the user is authenticated but lacks permission for this
    // resource. Silent, except the role ceiling (D207, D208): giving a role,
    // or changing an administrator or a role above your own, says why.
    if (status === 403 && !isCeilingRefusal(error.response?.data?.errorCode)) {
      return Promise.reject(error);
    }

    // The billing paywall (adding an employee past the free tier): the
    // upgrade dialog (`BillingPaywallListener`) explains it instead of a toast.
    const seatLimit = getSeatLimitDetails(error);
    if (seatLimit) {
      window.dispatchEvent(
        new CustomEvent(BILLING_SEAT_LIMIT_EVENT, { detail: seatLimit })
      );
      return Promise.reject(error);
    }

    // Whether billing applies is read on every Organization visit; a failed
    // read only hides the Billing card, so it never toasts.
    if (url?.endsWith('/billing/access')) {
      return Promise.reject(error);
    }

    // The document preview explains its refusals inline (French required in
    // Quebec, salary access), next to the controls that fix them; the live
    // audience count simply shows no number while the audience is unfinished.
    if (
      url?.endsWith('/document-templates/preview') ||
      url?.endsWith('/document-broadcasts/audience-count')
    ) {
      return Promise.reject(error);
    }

    // Composing a signing request (send or send for approval): the send page
    // explains the refusal inline, listing the fields still without a value.
    const method = error.config?.method?.toLowerCase();
    if (
      (method === 'post' || method === 'put') &&
      /\/signature-requests(\/[0-9a-f-]{36})?$/.test(url ?? '')
    ) {
      return Promise.reject(error);
    }

    // Sending a broadcast: the dialog shows the refusal next to its button
    // (a toast behind the dialog went unseen, fresh-eyes QA-04).
    if (method === 'post' && /\/document-broadcasts$/.test(url ?? '')) {
      return Promise.reject(error);
    }

    // Publishing a training: the publish dialog lists every issue inline.
    if (
      method === 'post' &&
      status === 422 &&
      /\/trainings\/[0-9a-f-]{36}\/publish$/.test(url ?? '')
    ) {
      return Promise.reject(error);
    }

    // Deleting an automatic review that is the default or already has reviews:
    // the page offers to turn it off instead.
    if (
      method === 'delete' &&
      status === 409 &&
      /\/performance\/evaluation-programs\/[0-9a-f-]{36}$/.test(url ?? '')
    ) {
      return Promise.reject(error);
    }

    // An automatic review's worked example hides itself on failure; the
    // "turn on" confirmation toasts its own failure.
    if (url?.endsWith('/performance/evaluation-programs/preview')) {
      return Promise.reject(error);
    }

    // The employee export dialog reads the error out of the blob body and
    // shows it inline (the 5,000-row cap asks for a filter).
    if (url?.endsWith('/employees/export')) {
      return Promise.reject(error);
    }

    // The payroll export page shows its refusals (period, row cap) inline.
    if (/\/payroll-export(\/download)?$/.test(url ?? '')) {
      return Promise.reject(error);
    }

    // The leave request form's day-count preview hides itself on failure;
    // the submit reports any real error.
    if (url?.endsWith('/day-count')) {
      return Promise.reject(error);
    }

    // Candidate messages keep email content out of generic error telemetry.
    // These components and Slack account-linking pages render their own errors.
    // The Slack account-linking pages render their own toast + inline error
    // for every failure on these endpoints — skip the generic one here to
    // avoid showing the same message twice.
    if (
      url?.includes('/recruiting/messages/') ||
      url?.includes('/slack/link-account') ||
      url?.includes('/admin/link-account')
    ) {
      return Promise.reject(error);
    }

    // A temporary group that changed while its move dialog was open: the
    // dialog refreshes the list and explains inline, so no second toast
    // (useAssignTemporaryGroup toasts the one case with no dialog left: the
    // group emptied).
    if (
      status === 409 &&
      url?.includes('/temporary-groups/assign') &&
      error.response?.data?.errorCode === ReportingErrorCode.GroupChanged
    ) {
      return Promise.reject(error);
    }

    // An email that belongs to an administrator of the company: the Add
    // employee form offers to link the record instead (D159), so no toast.
    if (
      status === 409 &&
      error.response?.data?.errorCode === 'EMAIL_BELONGS_TO_USER' &&
      error.response?.data?.errorDetails?.canLink === true
    ) {
      return Promise.reject(error);
    }

    // Show error toast only for non-401/403 errors (401 is handled silently above)

    const toastMessage = getUserErrorMessage(error);
    const sentryMessage = getSentryErrorMessage(error, 'Company Error');

    toast.dismiss();
    toast.error(toastMessage);
    const shouldLogToSentry =
      !status || // network / CORS / timeout
      status >= 500 || // backend crash
      status === 400; // suspicious bad request

    // Workflow templates may contain private message/email copy. Keep the
    // existing translated error toast, but never capture the request body.
    const privateWorkflowCopy =
      /\/(?:onboarding|offboarding)\/task-lists\/[^/]+\/tasks(?:\/[^/]+)?$/.test(
        url ?? ''
      );
    if (shouldLogToSentry && !privateWorkflowCopy) {
      Sentry.captureException(new Error(sentryMessage), {
        tags: {
          type: 'api_error',
          endpoint: url,
          method: error.config?.method,
          status: status?.toString(),
        },
        extra: {
          originalError: error,
          response: error.response?.data,
        },
      });
    }

    return Promise.reject(error);
  }
);

export default api;
